import type { Party } from '../../shared/interfaces';
import type {
  ImportAccountPreMappedEntry,
  ImportAccountRow,
  ImportAccountRowStatus,
  ParsedUserName,
} from '../../shared/importAccountTypes';

export function parseImportUserName(raw: string): ParsedUserName {
  const trimmed = raw.trim();
  const match = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (match) {
    return {
      raw: trimmed,
      loginId: match[1].trim(),
      displayName: match[2].trim(),
    };
  }
  return {
    raw: trimmed,
    loginId: trimmed,
    displayName: trimmed,
  };
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function rowWithParty(
  row: ImportAccountRow,
  party: Party,
  status: ImportAccountRowStatus,
): ImportAccountRow {
  const code = party.partyCode.toUpperCase();
  return {
    ...row,
    matchedPartyId: party.id,
    matchedPartyCode: party.partyCode,
    matchedPartyName: party.partyName,
    partyCodeInput: `${party.partyCode} - ${party.partyName}`,
    validatedRowPartyCode: code,
    partyInputError: '',
    status,
  };
}

function rowWithoutParty(row: ImportAccountRow, status: ImportAccountRowStatus = 'pending'): ImportAccountRow {
  return {
    ...row,
    partyCodeInput: row.partyCodeInput || '',
    validatedRowPartyCode: '',
    matchedPartyId: undefined,
    matchedPartyCode: undefined,
    matchedPartyName: undefined,
    partyInputError: '',
    status,
  };
}

/** Exact match: party code or name equals login/display from the import row. */
function findExactPartyMatch(parties: Party[], row: ImportAccountRow): Party | null {
  const login = normalize(row.loginId);
  const display = normalize(row.displayName);

  const byCode = parties.find((p) => normalize(p.partyCode) === login);
  if (byCode) return byCode;

  const byName = parties.find(
    (p) =>
      normalize(p.partyName) === display ||
      normalize(p.partyName) === login ||
      normalize(p.partyCode) === display,
  );
  return byName ?? null;
}

/** Fuzzy match: partial string overlap when exact match fails. */
function findFuzzyPartyMatch(parties: Party[], row: ImportAccountRow): Party | null {
  const login = normalize(row.loginId);
  const display = normalize(row.displayName);
  const raw = normalize(row.userNameRaw);

  const partial = parties.find((p) => {
    const code = normalize(p.partyCode);
    const name = normalize(p.partyName);
    if (!code && !name) return false;
    if (login && (code === login || name === login)) return true;
    if (display && (code === display || name === display)) return true;
    if (display.length >= 3 && name.includes(display)) return true;
    if (login.length >= 3 && (name.includes(login) || code.includes(login))) return true;
    if (raw.length >= 3 && (raw.includes(code) || raw.includes(name))) return true;
    return false;
  });
  return partial ?? null;
}

export function findPartyForImportRow(parties: Party[], row: ImportAccountRow): Party | null {
  return findExactPartyMatch(parties, row) ?? findFuzzyPartyMatch(parties, row);
}

function findPartyByCode(parties: Party[], code: string): Party | null {
  const normalized = normalize(code);
  return parties.find((p) => normalize(p.partyCode) === normalized) ?? null;
}

function preMappedKey(userNameRaw: string): string {
  return normalize(userNameRaw);
}

export function buildPreMappedLookup(
  entries: ImportAccountPreMappedEntry[],
): Map<string, ImportAccountPreMappedEntry> {
  const map = new Map<string, ImportAccountPreMappedEntry>();
  for (const entry of entries) {
    if (entry.loginId) {
      const loginKey = normalize(entry.loginId);
      if (loginKey) map.set(loginKey, entry);
    }
    const nameKey = preMappedKey(entry.userNameRaw);
    if (nameKey) map.set(nameKey, entry);
  }
  return map;
}

/** Apply saved username → party mappings from the backend (status: pre_mapped). */
export function applyPreMappedParties(
  rows: ImportAccountRow[],
  preMapped: ImportAccountPreMappedEntry[],
  parties: Party[],
): ImportAccountRow[] {
  if (preMapped.length === 0) return rows;

  const lookup = buildPreMappedLookup(preMapped);

  return rows.map((row) => {
    if (row.status === 'mapped') return row;

    const saved =
      lookup.get(normalize(row.loginId)) ?? lookup.get(preMappedKey(row.userNameRaw));
    if (!saved?.partyCode && saved?.partyId == null) return row;

    const party =
      (saved.partyId != null ? parties.find((p) => p.id === saved.partyId) : null) ??
      findPartyByCode(parties, saved.partyCode);

    if (!party) {
      return rowWithoutParty(row, 'pending');
    }

    return rowWithParty(row, party, 'pre_mapped');
  });
}

/** Apply fuzzy suggestions for rows that are not yet pre_mapped or mapped. */
export function applyFuzzyPartySuggestions(
  rows: ImportAccountRow[],
  parties: Party[],
): ImportAccountRow[] {
  return rows.map((row) => {
    if (row.status === 'pre_mapped' || row.status === 'mapped') return row;

    const exact = findExactPartyMatch(parties, row);
    if (exact) return rowWithParty(row, exact, 'suggested');

    const fuzzy = findFuzzyPartyMatch(parties, row);
    if (fuzzy) return rowWithParty(row, fuzzy, 'suggested');

    return rowWithoutParty(row, 'pending');
  });
}

/** @deprecated Use applyPreMappedParties + applyFuzzyPartySuggestions instead. */
export function applyPartyMatches(
  rows: ImportAccountRow[],
  parties: Party[],
): ImportAccountRow[] {
  return applyFuzzyPartySuggestions(rows, parties);
}

export function confirmSuggestedRow(row: ImportAccountRow): ImportAccountRow {
  if (row.status !== 'suggested' || !row.matchedPartyId || !row.validatedRowPartyCode) {
    return row;
  }
  return { ...row, status: 'mapped' };
}
